import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  addJobApplicantLinkRequest,
  deleteJobApplicantLinkRequest,
  getJobApplicantFeatureRequest,
  getJobApplicantLinksRequest,
  parseJobApplicantError,
} from 'api/requests/job-applicant-link-requests';
import {
  JOB_APPLICANTS__ADD_LINK,
  JOB_APPLICANTS__DELETE_LINK,
  JOB_APPLICANTS__GET_FEATURE,
  JOB_APPLICANTS__GET_LINKS,
  JOB_APPLICANTS__SET_ERROR,
  JOB_APPLICANTS__SET_FEATURE,
  JOB_APPLICANTS__SET_LINKS,
  JOB_APPLICANTS__SET_LOADING,
  JOB_APPLICANTS__SET_SUCCESS,
} from 'store/constants';
import { IJobApplicantLink } from 'types/employee/IJobApplicantLink';
import { GENERAL__ERROR_MESSAGE } from 'translations/constants';
import logError from 'utils/log-error';

export const setJobApplicantLinksAction = createAction<IJobApplicantLink[]>(JOB_APPLICANTS__SET_LINKS);
export const setJobApplicantLinksLoadingAction = createAction<boolean>(JOB_APPLICANTS__SET_LOADING);
export const setJobApplicantLinkSuccessAction = createAction<boolean>(JOB_APPLICANTS__SET_SUCCESS);
export const setJobApplicantLinkErrorAction =
  createAction<{ error: string | null; rejectedLocationIds?: number[] }>(JOB_APPLICANTS__SET_ERROR);
export const setJobApplicantFeatureAction =
  createAction<{ companyId: number; enabled: boolean }>(JOB_APPLICANTS__SET_FEATURE);

/**
 * Resolve the per-company feature state for an Admin, who has no company on their profile.
 * A failure (404 unknown company, or the role not being Admin after all) resolves to
 * disabled — the surface stays hidden rather than showing controls that would 403.
 */
export const getJobApplicantFeature = createAsyncThunk(
  JOB_APPLICANTS__GET_FEATURE,
  async (companyId: number, thunkApi) => {
    try {
      const enabled = await getJobApplicantFeatureRequest(companyId);
      thunkApi.dispatch(setJobApplicantFeatureAction({ companyId, enabled }));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(setJobApplicantFeatureAction({ companyId, enabled: false }));
    }
  },
);

/** Translate an error key client-side; fall back to the generic message when the API sends
 * a shape we don't recognise. */
const toErrorPayload = (error: unknown): { error: string; rejectedLocationIds: number[] } => {
  const parsed = parseJobApplicantError(error);
  return {
    error: parsed.key ?? GENERAL__ERROR_MESSAGE,
    rejectedLocationIds: parsed.details.locationIds ?? [],
  };
};

export const getJobApplicantLinks = createAsyncThunk(JOB_APPLICANTS__GET_LINKS, async (companyId: number, thunkApi) => {
  thunkApi.dispatch(setJobApplicantLinksLoadingAction(true));
  thunkApi.dispatch(setJobApplicantLinkErrorAction({ error: null }));
  try {
    const response = await getJobApplicantLinksRequest(companyId);
    thunkApi.dispatch(setJobApplicantLinksAction(response));
  } catch (error) {
    logError(error);
    thunkApi.dispatch(setJobApplicantLinkErrorAction(toErrorPayload(error)));
  }
  thunkApi.dispatch(setJobApplicantLinksLoadingAction(false));
});

export const addJobApplicantLink = createAsyncThunk(
  JOB_APPLICANTS__ADD_LINK,
  async (props: { companyId: number; locationIds: number[] }, thunkApi) => {
    thunkApi.dispatch(setJobApplicantLinksLoadingAction(true));
    thunkApi.dispatch(setJobApplicantLinkErrorAction({ error: null }));
    thunkApi.dispatch(setJobApplicantLinkSuccessAction(false));
    try {
      await addJobApplicantLinkRequest(props.companyId, { locationIds: props.locationIds });
      thunkApi.dispatch(setJobApplicantLinkSuccessAction(true));
      await thunkApi.dispatch(getJobApplicantLinks(props.companyId));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(setJobApplicantLinkErrorAction(toErrorPayload(error)));
    }
    thunkApi.dispatch(setJobApplicantLinksLoadingAction(false));
  },
);

export const deleteJobApplicantLink = createAsyncThunk(
  JOB_APPLICANTS__DELETE_LINK,
  async (props: { companyId: number; id: number }, thunkApi) => {
    thunkApi.dispatch(setJobApplicantLinksLoadingAction(true));
    try {
      await deleteJobApplicantLinkRequest(props.companyId, props.id);
      await thunkApi.dispatch(getJobApplicantLinks(props.companyId));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(setJobApplicantLinkErrorAction(toErrorPayload(error)));
    }
    thunkApi.dispatch(setJobApplicantLinksLoadingAction(false));
  },
);
